import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo } from 'react'
import styled from 'styled-components'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { ModalElementContext } from 'contexts/ModalProvider'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListItem from 'LuxKit/List/ListItem'
import config from 'constants/config'
import TextButton from 'LuxKit/Button/TextButton'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { last, sum } from 'lib/array/arrayUtils'
import { fetchAvailableCredit } from 'actions/AuthActions'
import CurrencyContext from 'contexts/currencyContext'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { removeRedeemedGiftCard } from 'actions/GiftCardActions'

import { RedeemedCard } from './common'
import CurrencyMismatchWarning from './CurrencyMismatchWarning'
import { useIntl } from 'react-intl'

const ListItemContainer = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const TotalContainer = styled.div`
  flex: 1;
`

interface Props {
  redeemedCards: Array<RedeemedCard>
  onRedeemAnother: () => void
  checkoutCurrency?: string
}

function Success({ redeemedCards, onRedeemAnother, checkoutCurrency }: Props) {
  const isGiftCardV2 = config.ENABLE_GIFT_CARD_V2
  const modalElement = useContext(ModalElementContext)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const modalBodyRef = React.useRef<HTMLDivElement>(null)
  const intl = useIntl()

  const currencyCode = last(redeemedCards)?.currencyCode

  const dispatch = useAppDispatch()

  const creditsLedger = useAppSelector((state) => state.auth.account.creditsLedger) ?? []
  const creditBalance = useAppSelector((state) => state.auth.account.creditsByCurrency[currencyCode])

  let expiresAt: string | undefined
  creditsLedger.forEach(credit => {
    if (!expiresAt || credit.expiresAt > expiresAt) {
      expiresAt = credit.expiresAt
    }
  })

  const total = sum(redeemedCards.filter(card => card.currencyCode === currencyCode), card => parseFloat(String(card.amount)))

  // Fetch credit balance
  useEffect(() => {
    if (currencyCode) {
      dispatch(fetchAvailableCredit(currencyCode))
    }
  }, [dispatch, currencyCode])

  useLayoutEffect(() => {
    if (modalBodyRef.current?.scrollTo) {
      // Scroll to bottom so the latest card and the "Redeem another card" button are visible
      modalBodyRef.current.scrollTo(0, modalBodyRef.current.scrollHeight)
    }
  }, [])

  const close = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const image = config.BRAND === 'luxuryescapes' ? getSVGImageUrl('payments-lecredit') : undefined

  const mismatchedCurrencies = useMemo(() => (
    checkoutCurrency ?
        [...new Set(
          redeemedCards
            .map(card => card.currencyCode)
            .filter(currency => currency !== checkoutCurrency),
        )] :
        []
  ), [checkoutCurrency, redeemedCards])

  const CardEndIcon = useCallback(({ code }: { code: string }) => {
    const handleRemoveCard = () => {
      dispatch(removeRedeemedGiftCard(code))
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{code}</span>
        <button
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleRemoveCard}
          aria-label={intl.formatMessage({
            id: 'RFGBZ2GQnf',
            defaultMessage: 'Remove card',
            description: 'Aria label for remove gift card button',
          })}
        >
          <LineTimesIcon />
        </button>
      </div>
    )
  }, [dispatch, intl])

  return (
    isGiftCardV2 && !isSpoofed ? (
      <>
        <ModalHeader
          title={intl.formatMessage({
            id: 'Utv2+sJxgG',
            defaultMessage: 'Gift Card added',
            description: 'Modal title when gift card is successfully added',
          })}
          onCloseButtonClick={close}
        />
        <ModalBody ref={modalBodyRef}>
          <ModalContent>
            <VerticalSpacer gap={24}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'aUpLRCIJqo',
                  defaultMessage: 'Available at checkout',
                  description: 'Message indicating gift card is available at checkout',
                })}
              </BodyText>
              <VerticalSpacer gap={8}>
                <div>
                  {redeemedCards.map((card) => {
                    return <ListItemContainer key={card.code}>
                      <ListItem
                        title={<BodyText variant="large" weight="bold"><FormatCurrency currency={card.currencyCode} value={card.amount} format="preciseDollar" /></BodyText>}
                        startIcon={image && <img src={image} width="34" height="24" alt="" />}
                        endIcon={<CardEndIcon code={card.code} />}
                      />
                    </ListItemContainer>
                  })}
                </div>

              </VerticalSpacer>

              {mismatchedCurrencies.length > 0 && (
                <CurrencyMismatchWarning checkoutCurrency={checkoutCurrency!} mismatchedCurrencies={mismatchedCurrencies} />
              )}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({
              id: 'jJSN7NpDj2',
              defaultMessage: 'Done',
              description: 'Button text to close modal',
            }),
            onClick: close,
          }}
        >
          <TotalContainer>
            <CurrencyContext.Provider value={currencyCode}>
              <Heading variant="heading4" wrap="no-wrap">
                <FormatCurrency currency={currencyCode} value={total} /> <Caption variant="large" as="span">
                  {intl.formatMessage({
                    id: 'MQfm0eAt0c',
                    defaultMessage: 'total',
                    description: 'Caption for total amount',
                  })}
                </Caption>
              </Heading>
            </CurrencyContext.Provider>
          </TotalContainer>
          <TextButton kind="tertiary" onClick={onRedeemAnother}>
            {intl.formatMessage({
              id: '5I/9/nnJfk',
              defaultMessage: 'Add another card',
              description: 'Button text to add another gift card',
            })}
          </TextButton>
        </ModalFooter>
      </>
    ) : (
      <>
        <ModalHeader
          title={intl.formatMessage({
            id: 'W5sfY9OlQZ',
            defaultMessage: 'Gift credit summary',
            description: 'Modal title for gift credit summary',
          })}
          onCloseButtonClick={close}
        />
        <ModalBody ref={modalBodyRef}>
          <ModalContent>
            <VerticalSpacer gap={24}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'O0gclkmVJt',
                  defaultMessage: 'Here is a summary of redeemed {brandName} gift cards',
                  description: 'Description text for gift credit summary',
                }, {
                  brandName: config.brandConfig.name,
                })}
              </BodyText>

              <VerticalSpacer gap={8}>
                <div>
                  {redeemedCards.map((card, i) => {
                    const isLast = i === redeemedCards.length - 1
                    let title
                    if (isLast) {
                      title = (
                        <BodyText variant="large" weight="bold">
                          {intl.formatMessage({
                            id: 'cLs4F8QRBz',
                            defaultMessage: 'Gift card redeemed!',
                            description: 'Title for last redeemed gift card',
                          })}
                        </BodyText>
                      )
                    } else {
                      title = intl.formatMessage({
                        id: '6TUJoGV1FI',
                        defaultMessage: 'Gift card redeemed',
                        description: 'Title for redeemed gift card',
                      })
                    }
                    return <ListItemContainer key={card.code}>
                      <ListItem
                        title={title}
                        subtitle={card.code}
                        startIcon={image && <img src={image} width="34" height="24" alt="" />}
                        endIcon={<FormatCurrency currency={card.currencyCode} value={card.amount} format="preciseDollar" />}
                      />
                    </ListItemContainer>
                  })}
                </div>

                <TextButton kind="tertiary" onClick={onRedeemAnother} startIcon={<LinePlusIcon />}>
                  {intl.formatMessage({
                    id: 'bUQgHfpoCb',
                    defaultMessage: 'Redeem another card',
                    description: 'Button text to redeem another gift card',
                  })}
                </TextButton>
              </VerticalSpacer>

              {mismatchedCurrencies.length > 0 && (
                <CurrencyMismatchWarning checkoutCurrency={checkoutCurrency!} mismatchedCurrencies={mismatchedCurrencies} />
              )}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({
              id: 'jJSN7NpDj2',
              defaultMessage: 'Done',
              description: 'Button text to close modal',
            }),
            onClick: close,
          }}
        >
          <TotalContainer>
            <CurrencyContext.Provider value={currencyCode}>
              <Heading variant="heading4" wrap="no-wrap">
                <FormatCurrency currency={currencyCode} value={total} /> <Caption variant="large" as="span">added</Caption>
              </Heading>
              {creditBalance?.loading && <Caption variant="large" colour="neutral-three">
                {intl.formatMessage({
                  id: 'yx7VBS6JWN',
                  defaultMessage: 'Fetching credit balance...',
                  description: 'Loading text while fetching credit balance',
                })}
              </Caption>}
              {typeof creditBalance?.balance === 'number' && !creditBalance.loading && <Caption variant="large" colour="neutral-three">
                <FormatCurrency currency={currencyCode} value={creditBalance.balance} /> credit balance
                {!!expiresAt && <>(Expires <FormatDateTime value={expiresAt} format="dmy-casual-short" />)</>}
              </Caption>}
            </CurrencyContext.Provider>
          </TotalContainer>
        </ModalFooter>
      </>
    )
  )
}

export default Success
